/**
 * IBAS News & Events data — single source of truth for the /news list page and
 * the /news/:slug detail pages.
 *
 * SOURCING (ibas-brand skill, HARD rule): every article, date, figure and quote
 * here is drawn from the live IBAS newsroom (https://ibas.edu.eu) — nothing is
 * invented. Accreditation and ranking claims are reproduced VERBATIM (QS ranks,
 * ISASAHE "PREMIER ACCREDITATION", accreditor names, dates).
 *
 * ATTACHMENTS: the detail page supports four attachment types — image, pdf,
 * video and link. EVERY attachment URL below was verified live (HTTP 200) before
 * inclusion; broken/guessed URLs were pruned. Notes on the two non-image types:
 *   • video → the OFFICIAL IBAS YouTube playlist embed (their own channel). No
 *     third-party "graduation" clip is misattributed to IBAS.
 *   • pdf   → the real, IBAS-hosted "Authorised Swiss Banks (FINMA)" student
 *     resource (application/pdf, verified). The QS/ISASAHE certificates exist on
 *     the live site only as page-render JPGs, never as downloadable PDFs, so they
 *     are carried as image attachments, not fabricated PDFs.
 *
 * Sources: /news/, /category/news/ and each article permalink on ibas.edu.eu.
 */

/* ── Types ─────────────────────────────────────────────────────────────────── */

export type NewsCategory =
  | 'Accreditation & Rankings'
  | 'Graduation'
  | 'Campus & Community'
  | 'Announcements';

/** The four supported attachment kinds, as a discriminated union. */
export interface NewsImageAttachment {
  type: 'image';
  url: string;
  label: string;
}
export interface NewsPdfAttachment {
  type: 'pdf';
  url: string;
  label: string;
  /** Optional human-readable size, shown on the download card. */
  size?: string;
}
export interface NewsVideoAttachment {
  type: 'video';
  /** 'youtube' → embeddable iframe URL; 'file' → direct .mp4/.webm source. */
  provider: 'youtube' | 'file';
  url: string;
  label: string;
}
export interface NewsLinkAttachment {
  type: 'link';
  url: string;
  label: string;
}
export type NewsAttachment =
  | NewsImageAttachment
  | NewsPdfAttachment
  | NewsVideoAttachment
  | NewsLinkAttachment;

export interface NewsArticle {
  slug: string;
  title: string;
  /** Machine date (YYYY-MM-DD) — used for sorting. */
  dateISO: string;
  /** Display date, UK order (e.g. "19 October 2025"). */
  dateDisplay: string;
  /** Normalised single category, used for the list-page filter. */
  category: NewsCategory;
  /** Original source categories/tags, shown as chips on the detail page. */
  tags: readonly string[];
  excerpt: string;
  body: readonly string[];
  /** Lead image (verified). */
  heroImage: string;
  /** How the lead image is fitted: photos crop ('cover'); certificates/badges/
   *  logos/portraits are shown whole ('contain'). */
  heroFit: 'cover' | 'contain';
  attachments: readonly NewsAttachment[];
}

/* ── Shared attachments ────────────────────────────────────────────────────── */

const IBAS_LINKEDIN: NewsLinkAttachment = {
  type: 'link',
  url: 'https://www.linkedin.com/school/international-business-academy-of-switzerland-gmbh',
  label: 'IBAS on LinkedIn',
};
const IBAS_FACEBOOK: NewsLinkAttachment = {
  type: 'link',
  url: 'https://www.facebook.com/IBAS.Education/',
  label: 'IBAS on Facebook',
};
/** The official IBAS YouTube playlist (their own channel) — embeddable. */
const IBAS_YOUTUBE: NewsVideoAttachment = {
  type: 'video',
  provider: 'youtube',
  url: 'https://www.youtube.com/embed/videoseries?list=PLR8R9EFFySdL5eomo_WpUTC72KCii2eoC',
  label: 'IBAS on YouTube — graduation & campus films',
};

/* ── Articles (newest first) ───────────────────────────────────────────────── */

export const NEWS: readonly NewsArticle[] = [
  {
    slug: 'a-small-celebration-a-big-step-forward',
    title: 'A Small Celebration, A Big Step Forward',
    dateISO: '2026-04-01',
    dateDisplay: '1 April 2026',
    category: 'Campus & Community',
    tags: ['Congratulations', 'Campus life', 'MBA'],
    excerpt:
      'On-site MBA students in Switzerland gathered — joined by the President of IBAS — to mark the completion of their academic phase before moving into internships or further modules.',
    body: [
      'Yesterday we had one of those moments on campus that feels simple, but actually means a lot. After a full year of academic work, many of our on-site MBA students in Switzerland have now completed their academic phase. So we took the time to bring everyone together for a small celebration. Nothing overly formal, just good food, snacks, conversations, and a chance to pause for a bit. We were also joined by the President of IBAS, which made the occasion feel even more special for the students.',
      'What made the moment meaningful was not just the celebration itself, but what it represents. For some students, this marks the transition into their internship phase, where everything they have learned starts to come to life in real working environments. For others, it means they have now fully completed their studies. And for some, the journey continues on campus as they complete their remaining modules. That mix is part of how the programme works, and it reflects the different paths and timelines each student brings with them.',
      'There is something nice about seeing all of those stages come together in one room. Some students looking ahead to their next step, others taking a moment to recognise that they have reached the end of a major chapter. At the same time, we are preparing to welcome our BBA students back on campus, as they return from their own internship phase.',
      'In many ways, this moment felt less like an ending and more like a transition — a chance to reflect on the work of the past year and to look ahead to what comes next. Sometimes it is these smaller, informal moments that stay with people the most. Not just the milestones themselves, but the chance to share them together.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2026/04/IMG_4613-scaled.jpg',
    heroFit: 'cover',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/04/IMG_4569-scaled.jpg', label: 'MBA celebration on campus' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/04/IMG_4598-scaled.jpg', label: 'Students together at the gathering' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/04/IMG_4615-scaled.jpg', label: 'Marking the milestone' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/04/IMG_4572-scaled.jpg', label: 'Conversation and celebration' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/04/IMG_4626-scaled.jpg', label: 'The President of IBAS joins the students' },
      { type: 'link', url: 'https://ibas.edu.eu/congratulations/a-small-celebration-a-big-step-forward/', label: 'Read on ibas.edu.eu' },
      IBAS_LINKEDIN,
      IBAS_FACEBOOK,
    ],
  },
  {
    slug: 'international-business-academy-of-switzerland-has-received-qs-accreditation-2025',
    title: 'IBAS has Received QS Accreditation 2025',
    dateISO: '2025-10-19',
    dateDisplay: '19 October 2025',
    category: 'Accreditation & Rankings',
    tags: ['Accreditation', 'QS Online MBA', 'QS Stars Rating', 'Quality Assurance', 'MBA'],
    excerpt:
      'IBAS has officially been ranked in the QS Online MBA Rankings 2026 — #19 in Europe, the Middle East and Africa, and #46 globally.',
    body: [
      'We are proud to announce that the International Business Academy of Switzerland (IBAS) has officially been ranked in the QS Online MBA Rankings 2026, marking a historic recognition of academic excellence and international impact.',
      'IBAS is ranked #19 in the QS Online MBA Rankings 2026: Europe, Middle East, and Africa, and #46 in the QS Online MBA Rankings 2026: Global.',
      'This prestigious ranking by QS (Quacquarelli Symonds) — one of the world’s most respected higher education evaluators — places IBAS among the leading Online MBA institutions in Europe, the Middle East, and Africa. It reflects our continuous commitment to delivering innovative, globally focused business education; ensuring high-quality learning experiences for our students; and fostering leadership and professional excellence across industries.',
      'This recognition is not just a milestone — it’s a testament to the hard work, vision, and dedication of our faculty, students, and partners worldwide. We thank our entire community for contributing to IBAS’s journey toward global excellence.',
    ],
    heroImage:
      'https://ibas.edu.eu/wp-content/uploads/2025/10/QS_20Certificate-Online_20MBA_20Europe-Middle_20East-Africa_202026_page-0001-1200x1696.jpg',
    heroFit: 'contain',
    attachments: [
      {
        type: 'image',
        url: 'https://ibas.edu.eu/wp-content/uploads/2025/10/QS_20Certificate-Online_20MBA_20Europe-Middle_20East-Africa_202026_page-0001-1200x1696.jpg',
        label: 'QS Online MBA 2026 — Europe, Middle East & Africa (#19) certificate',
      },
      {
        type: 'image',
        url: 'https://ibas.edu.eu/wp-content/uploads/2025/10/QS_20Certificate-Online_20MBA_202026_page-0001-1200x1696.jpg',
        label: 'QS Online MBA 2026 — Global (#46) certificate',
      },
      { type: 'link', url: 'https://ibas.edu.eu/accreditation/international-business-academy-of-switzerland-has-received-qs-accreditation-2025/', label: 'Read on ibas.edu.eu' },
      { type: 'link', url: 'https://www.topmba.com/online-mba-rankings-global', label: 'QS Online MBA Rankings (topmba.com)' },
      IBAS_LINKEDIN,
    ],
  },
  {
    slug: 'thank-you-to-ibas-alumna-sirithakorn-srinitioj-nickname-chisa-for-joining-us-as-a-guest-speaker',
    title: 'Guest speaker: IBAS alumna Sirithakorn Srinitioj (Chisa)',
    dateISO: '2025-05-01',
    dateDisplay: '1 May 2025',
    category: 'Campus & Community',
    tags: ['Alumni', 'Guest speaker', 'Campus life'],
    excerpt:
      'IBAS welcomed alumna Sirithakorn Srinitioj (Chisa), MBA Class of 2018 and now a business owner in Switzerland, back to campus as a guest speaker.',
    body: [
      'We were delighted to welcome IBAS alumna Sirithakorn Srinitioj (nickname: Chisa), MBA Class of 2018, back to campus as a guest speaker.',
      'Now a successful business owner based in Switzerland, Chisa returned to IBAS to share her personal and professional journey with current students. During her session, she reflected on her experience as an MBA student at IBAS, the path that led her to entrepreneurship, and the realities of building a business as an international professional in Switzerland.',
      'Her talk offered valuable insights into navigating cross-cultural business environments, adapting to new markets, and turning academic learning into practical, real-world application. Students had the opportunity to engage directly with her experiences, ask questions, and gain first-hand perspective on an international business career.',
      'It was a pleasure to welcome Chisa back to IBAS and to see our alumni continue to contribute to the academic and professional life of the school. Her visit highlighted the strength of the IBAS alumni community and the connections that continue well beyond graduation.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2025/05/20250429_1051520-scaled.jpg',
    heroFit: 'cover',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2025/05/IMG20250429101630-scaled.jpg', label: 'Guest-speaker session' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2025/05/20250429_105355-scaled.jpg', label: 'Chisa with IBAS students' },
      { type: 'link', url: 'https://ibas.edu.eu/uncategorized/thank-you-to-ibas-alumna-sirithakorn-srinitioj-nickname-chisa-for-joining-us-as-a-guest-speaker/', label: 'Read on ibas.edu.eu' },
      IBAS_LINKEDIN,
    ],
  },
  {
    slug: 'welcome-march-2025-on-site-students',
    title: 'Welcome, March 2025 On-Site Students!',
    dateISO: '2025-04-14',
    dateDisplay: '14 April 2025',
    category: 'Campus & Community',
    tags: ['Campus life', 'Orientation', 'BBA', 'MBA'],
    excerpt:
      'IBAS welcomed new BBA and MBA students to its Thalwil campus for the March 2025 intake, with orientation introducing them to faculty and campus life.',
    body: [
      'Earlier this month, we welcomed our new BBA and MBA students to IBAS — International Business Academy of Switzerland.',
      'During the orientation, students had the chance to meet our faculty, explore the campus in Thalwil, and get a head start on their academic journey. It was a great opportunity to connect, ask questions, and prepare for the months ahead.',
      'At IBAS, we focus on more than just education — we build pathways to professional growth, international exposure, and long-term success.',
      'We’re excited to see what this new group will achieve.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2025/04/20250410_160814-scaled.jpg',
    heroFit: 'cover',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2025/04/20250411_125449-scaled.jpg', label: 'Students at the Thalwil campus' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2025/04/WhatsApp-Image-2025-04-01-at-14.50.03_06bec502.jpg', label: 'March 2025 intake' },
      { type: 'link', url: 'https://ibas.edu.eu/uncategorized/welcome-march-2025-on-site-students/', label: 'Read on ibas.edu.eu' },
    ],
  },
  {
    slug: 'ibas-graduation-11th-january-2025',
    title: 'IBAS Graduation, 11 January 2025',
    dateISO: '2025-01-11',
    dateDisplay: '11 January 2025',
    category: 'Graduation',
    tags: ['Graduation', 'Student Success', 'MBA', 'DBA', 'IBAS Community'],
    excerpt:
      'IBAS celebrated its MBA and DBA graduates at the My Global World One World Graduation Ceremony, held at the PAUM Clubhouse in Kuala Lumpur, Malaysia.',
    body: [
      'We proudly celebrated a significant milestone for our IBAS graduates and academic community at the My Global World One World Graduation Ceremony, held in Kuala Lumpur, Malaysia, at the PAUM Clubhouse.',
      'The ceremony brought together graduates, faculty, leadership, and guests to honour the achievements of our MBA and DBA graduates, marking the successful completion of years of dedication, perseverance, and academic commitment.',
      'Graduation is more than a formal conclusion to a programme — it is recognition of resilience, discipline, and personal growth. Our MBA and DBA graduates have balanced professional responsibilities, personal commitments, and academic demands throughout their studies.',
      'Holding the graduation in Malaysia also highlighted the international nature of our academic community, bringing together graduates from diverse backgrounds and professional journeys — a reflection of a global community committed to lifelong learning and professional excellence.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2026/02/G0651-scaled.jpg',
    heroFit: 'cover',
    attachments: [
      IBAS_YOUTUBE,
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/G0657-320x202.jpg', label: 'Graduation ceremony, Kuala Lumpur' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/G0667-320x202.jpg', label: 'Graduates on stage' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/G0192-320x202.jpg', label: 'Ceremony moment' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/G0242-320x202.jpg', label: 'Graduation ceremony' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/G0294-320x202.jpg', label: 'Graduates and faculty' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/ADIB0048-320x202.jpg', label: 'One World Graduation Ceremony' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/ADIB0056-320x202.jpg', label: 'Celebrating the graduates' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/ADIB0064-320x202.jpg', label: 'Graduation portraits' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2026/02/ADIB0084-320x202.jpg', label: 'A shared moment of accomplishment' },
      { type: 'link', url: 'https://ibas.edu.eu/student-success/ibas-graduation-11th-january-2025/', label: 'Read on ibas.edu.eu' },
    ],
  },
  {
    slug: 'graduates-from-our-partner-myanmar-commercial-university-myanmar',
    title: 'Graduates from Our Partner: Myanmar Commercial University',
    dateISO: '2024-11-18',
    dateDisplay: '18 November 2024',
    category: 'Graduation',
    tags: ['Graduation', 'Partnership', 'MBA', 'DBA', 'Student Success'],
    excerpt:
      'IBAS congratulates the MBA and DBA graduates from partner institution Myanmar Commercial University (MCU), and the leaders behind them.',
    body: [
      'Congratulations to our distinguished graduates. This remarkable achievement reflects your unwavering dedication, resilience, and pursuit of excellence. Today, you join a proud legacy of alumni who have made a meaningful impact across industries and communities worldwide.',
      'The article profiles three leaders from Myanmar Commercial University: Dr. Zin Ko Ko, a distinguished author, seasoned researcher, and accomplished edupreneur serving as founder and chancellor of MCU; Dr. Nan Wai Linn, a professor, lecturer, and mentor specialising in management and education, and Vice Principal of MCU; and Dr. Nay Tun Aung, Director of General Administration at MCU, overseeing operational efficiency, resource management, and administrative excellence.',
      'We celebrate your accomplishments and look forward to witnessing the incredible contributions you will make to the world. Well done, graduates!',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2024/11/IBAS-External-Assessor-Zin-KoKo-300x300-1.png',
    heroFit: 'contain',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/11/IBAS-External-Assessor-Zin-KoKo-300x300-1.png', label: 'Dr. Zin Ko Ko — Founder & Chancellor, MCU' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/11/IBAS-External-Assessor-Nan-Wai-Linn-300x300-1.png', label: 'Dr. Nan Wai Linn — Vice Principal, MCU' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/11/462562993_1243034730257864_7945120164525971195_n-150x150.png', label: 'Dr. Nay Tun Aung — Director of General Administration, MCU' },
      { type: 'link', url: 'https://ibas.edu.eu/congratulations/graduates-from-our-partner-myanmar-commercial-university-myanmar/', label: 'Read on ibas.edu.eu' },
    ],
  },
  {
    slug: 'international-business-academy-of-switzerland-has-received-isasahe-accreditation',
    title: 'IBAS has Received ISASAHE Accreditation',
    dateISO: '2024-08-01',
    dateDisplay: '1 August 2024',
    category: 'Accreditation & Rankings',
    tags: ['Accreditation', 'Quality Assurance', 'QA Audit'],
    excerpt:
      'IBAS announces it has received Premier Accreditation from the International Swiss Accreditation Services for Adult & Higher Education (ISASAHE).',
    body: [
      'Congratulations to the International Business Academy of Switzerland for achieving PREMIER ACCREDITATION from International Swiss Accreditation Services for Adult & Higher Education (ISASAHE).',
      'The announcement is accompanied by the official ISASAHE Premier Accreditation badge set, marking recognition of IBAS’s adult and higher education provision by the accrediting body.',
      'At IBAS, our vision is to make education affordable and accessible — so you can learn anywhere, any time, and in any learning method that matches your personality.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2024/10/ISASAHE_Premier_Accreditation_4_Badges-e1729225625162.png',
    heroFit: 'contain',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/10/ISASAHE_Premier_Accreditation_4_Badges-e1729225625162.png', label: 'ISASAHE Premier Accreditation badge set' },
      { type: 'link', url: 'https://isasahe.org/institutions/international-business-academy-of-switzerland/', label: 'IBAS on the ISASAHE register' },
      { type: 'link', url: 'https://ibas.edu.eu/education/international-business-academy-of-switzerland-has-received-isasahe-accreditation/', label: 'Read on ibas.edu.eu' },
    ],
  },
  {
    slug: 'new-onsite-office-hours',
    title: 'New On-Site Office Hours',
    dateISO: '2024-05-21',
    dateDisplay: '21 May 2024',
    category: 'Announcements',
    tags: ['Announcement', 'Student services', 'On-site'],
    excerpt:
      'IBAS shares its updated on-site office hours — Wednesdays and Fridays, 09:00–12:00, until September 2024.',
    body: [
      'Until September 2024, IBAS on-site office hours are: Wednesdays, 09:00 – 12:00 and Fridays, 09:00 – 12:00.',
      'For any enquiries outside the office hours, please contact our staff via email at admissions@ibas.edu.eu.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2026/07/IBAS-Logo-Version-2-small.png',
    heroFit: 'contain',
    attachments: [
      {
        type: 'pdf',
        url: 'https://ibas.edu.eu/wp-content/uploads/2022/04/Authorised_Banks-FINMA-07-2021.pdf',
        label: 'Authorised Swiss Banks (FINMA) — student banking resource',
        size: 'PDF',
      },
      { type: 'link', url: 'mailto:admissions@ibas.edu.eu', label: 'Email admissions@ibas.edu.eu' },
      { type: 'link', url: 'https://ibas.edu.eu/in-the-news/new-onsite-office-hours/', label: 'Read on ibas.edu.eu' },
    ],
  },
  {
    slug: 'qs-online-mba-ranking-2024',
    title: 'QS Online MBA Ranking 2024',
    dateISO: '2024-04-25',
    dateDisplay: '25 April 2024',
    category: 'Accreditation & Rankings',
    tags: ['QS Online MBA', 'Rankings', 'MBA', 'Online Learning'],
    excerpt:
      'IBAS celebrates its 2024 QS Online MBA Rankings placement: #2 in Switzerland, #19 in Europe, the Middle East and Africa, and #56 globally.',
    body: [
      'We’re thrilled to share exciting news. In the latest Quacquarelli Symonds (QS) Online MBA Rankings for 2024, IBAS has achieved remarkable standings.',
      '#2 in QS Online MBA Rankings 2024: Switzerland. #19 in QS Online MBA Rankings 2024: Europe, Middle East, and Africa. #56 in QS Online MBA Rankings 2024: Global.',
      'Our Online MBA programmes are designed to provide exceptional management education, adapting to contemporary needs to deliver high-calibre training in business leadership. These rankings affirm our dedication to educational excellence and the success of our faculty and students.',
      'Check out the QS Online MBA Rankings 2024 at topmba.com.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2024/04/QS-Global-Online-MBA-Ranking-2024-Switzerland1.jpg',
    heroFit: 'contain',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/04/QS-Global-Online-MBA-Ranking-2024-Switzerland1.jpg', label: 'QS Online MBA 2024 — Switzerland (#2)' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/04/20663775-International-Business-Academy-of-Switzerland-IBAS-Europe-Middle-East-and-Africa-728x1024.jpg', label: 'QS Online MBA 2024 — Europe, Middle East & Africa (#19)' },
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/04/QS-Ranking-56-20663775-International-Business-Academy-of-Switzerland-IBAS-Global-2024-FULL-NAME.pdf-728x1024.jpg', label: 'QS Online MBA 2024 — Global (#56)' },
      { type: 'link', url: 'https://www.topmba.com/online-mba-rankings-global', label: 'QS Online MBA Rankings 2024 (topmba.com)' },
      { type: 'link', url: 'https://ibas.edu.eu/in-the-news/qs-online-mba-ranking-2024/', label: 'Read on ibas.edu.eu' },
    ],
  },
  {
    slug: 'launch-of-the-doctor-of-business-administration',
    title: 'Launch of the Doctor of Business Administration (DBA)',
    dateISO: '2024-03-22',
    dateDisplay: '22 March 2024',
    category: 'Announcements',
    tags: ['DBA', 'Partnership', 'Learning Center', 'Online Learning'],
    excerpt:
      'IBAS announces the launch of its Doctor of Business Administration programme through approved learning center EDUK8U Grad School Asia.',
    body: [
      'We’re thrilled to announce the launch of the Doctor of Business Administration (DBA) programme through International Business Academy of Switzerland (IBAS) approved learning center, EDUK8U Grad School Asia.',
      'This collaboration was officially signed by Martin Nielsen, Chief Executive Officer of My Global World Education Group, and Hon. Professor Dr Roy Prasad, Group MD & Principal Executive Officer of EDUK8U.',
      'EDUK8U Grad School Asia serves as an approved learning center for the International Business Academy of Switzerland (IBAS), ensuring the highest standards of education.',
      'Stay tuned for more updates as we continue to expand educational opportunities and empower future leaders in the global arena.',
    ],
    heroImage: 'https://ibas.edu.eu/wp-content/uploads/2024/03/SIGNING-CEREMONY-3-300x169.jpg',
    heroFit: 'cover',
    attachments: [
      { type: 'image', url: 'https://ibas.edu.eu/wp-content/uploads/2024/03/SIGNING-CEREMONY-3-300x169.jpg', label: 'DBA signing ceremony — Martin Nielsen and Dr Roy Prasad' },
      { type: 'link', url: 'https://ibas.edu.eu/in-the-news/launch-of-the-doctor-of-business-administration/', label: 'Read on ibas.edu.eu' },
    ],
  },
];

/* ── Helpers ───────────────────────────────────────────────────────────────── */

/** Category filter order for the list page ("All" is prepended in the UI). */
export const NEWS_CATEGORIES: readonly NewsCategory[] = [
  'Accreditation & Rankings',
  'Graduation',
  'Campus & Community',
  'Announcements',
];

/** Look up a single article by its URL slug (undefined if unknown). */
export function getNewsArticle(slug: string | undefined): NewsArticle | undefined {
  return NEWS.find((a) => a.slug === slug);
}

/** Related articles — same category first, then most recent, excluding self. */
export function relatedNews(article: NewsArticle, count = 3): readonly NewsArticle[] {
  const sameCategory = NEWS.filter(
    (a) => a.slug !== article.slug && a.category === article.category,
  );
  const rest = NEWS.filter(
    (a) => a.slug !== article.slug && a.category !== article.category,
  );
  return [...sameCategory, ...rest].slice(0, count);
}

/** Count attachments of a given type on an article (used for card meta). */
export function countAttachments(article: NewsArticle, type: NewsAttachment['type']): number {
  return article.attachments.filter((a) => a.type === type).length;
}
